import { useEffect, useState } from 'react';
import { Container, Typography, Box, Fab } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Session } from '@/types/session';
import { sessionStorage } from '@services/storage/session-storage';
import { SessionList } from '@components/session/SessionList';
import { SessionFilters } from '@components/session/SessionFilters';
import { SessionActions } from '@components/session/SessionActions';
import { Loading } from '@components/common/Loading';
import { useSessionFilters } from '@hooks/useSessionFilters';
import { useSessionSelection } from '@hooks/useSessionSelection';
import { logger } from '@utils/logger';

export function Home() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const allSessions = await sessionStorage.getAll();
      setSessions(allSessions);
    } catch (error) {
      logger.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const {
    filters,
    filteredSessions,
    availableWards,
    activeFilterCount,
    setFilter,
    clearFilters,
  } = useSessionFilters(sessions);

  const {
    selectedIds,
    selectedCount,
    allSelected,
    someSelected,
    selectedSessionIds,
    toggle,
    selectAll,
    clearAll,
  } = useSessionSelection(filteredSessions);

  if (loading) {
    return <Loading message={t('home.loadingSessions')} />;
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }} className="no-print">
        <Typography variant="h4" component="h1" gutterBottom>
          {t('home.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('home.subtitle')}
        </Typography>
      </Box>

      {/* Filters */}
      <Box className="no-print">
        <SessionFilters
          filters={filters}
          availableWards={availableWards}
          activeFilterCount={activeFilterCount}
          onFilterChange={setFilter}
          onClearFilters={clearFilters}
        />
      </Box>

      {/* Results count */}
      {activeFilterCount > 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1 }}
          className="no-print"
        >
          {t('filters.resultsCount', { count: filteredSessions.length })}
        </Typography>
      )}

      <SessionList
        sessions={filteredSessions}
        selectable
        selectedIds={selectedIds}
        onSelectionToggle={toggle}
        onSelectAll={selectAll}
        onDeselectAll={clearAll}
        allSelected={allSelected}
        someSelected={someSelected}
      />

      {selectedCount > 0 && (
        <SessionActions
          selectedCount={selectedCount}
          selectedSessionIds={selectedSessionIds}
          onClearSelection={clearAll}
        />
      )}

      <Fab
        color="primary"
        aria-label={t('home.newSession')}
        className="no-print"
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 16,
        }}
        onClick={() => navigate('/session/new')}
      >
        <Add />
      </Fab>
    </Container>
  );
}
